<template>
  <article
    :class="[
      'bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md',
      isFeatured ? 'grid md:grid-cols-2 gap-0' : '',
    ]"
  >
    <div
      :class="[
        'relative overflow-hidden',
        isFeatured ? 'h-full min-h-[150px]' : 'h-48',
      ]"
    >
      <NuxtLink :to="`/buzz/${post.id}`">
        <img
          :src="
            post.attributes.cover?.data?.attributes?.url ||
            '/images/placeholder-blog.jpg'
          "
          :alt="post.attributes.title"
          class="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
        />

        <div
          class="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"
        ></div>
      </NuxtLink>
    </div>

    <div class="p-6 relative">
      <!-- Category Badge - Floating on top for featured posts -->
      <div
        v-if="isFeatured && post.attributes.categories?.data?.length"
        class="absolute -top-5 right-6 px-3 py-1.5 bg-primary text-white rounded-full text-xs font-medium shadow-sm transform hover:scale-105 transition-transform"
      >
        {{ post.attributes.categories.data[0].attributes.name }}
      </div>

      <!-- Post Meta with Categories -->
      <div class="flex items-center gap-4 text-sm mb-4">
        <NuxtTime
          :datetime="post.attributes.publishedAt"
          format="MMMM D, YYYY"
          class="text-gray-500 flex items-center gap-1"
        >
          <template #default="{ formatted }">
            <Icon name="heroicons:calendar" class="w-4 h-4 text-primary/70" />
            <span>{{ formatted }}</span>
          </template>
        </NuxtTime>

        <div class="flex flex-wrap gap-2">
          <span
            v-for="category in post.attributes.categories?.data"
            :key="category.id"
            class="px-2 py-1 bg-primary/5 text-primary rounded-full text-xs font-medium transform hover:scale-105 hover:bg-primary/10 transition-all cursor-pointer"
          >
            {{ category.attributes.name }}
          </span>
        </div>
      </div>

      <!-- Title with hover effect -->
      <h2
        :class="[
          'font-bold text-gray-900 mb-4 transition-colors duration-300 hover:text-primary',
          isFeatured ? 'text-3xl' : 'text-xl',
        ]"
      >
        {{ post.attributes.title }}
      </h2>

      <!-- Post Preview with subtle animation -->
      <div
        class="prose prose-sm text-gray-600 line-clamp-3 mb-4 group-hover:line-clamp-none transition-all duration-500"
      >
        <MDC
          :value="
            post.attributes.excerpt ||
            post.attributes.body.substring(0, 150) + '...'
          "
        />
      </div>

      <!-- Read More Link with enhanced interactions -->
      <NuxtLink
        :to="`/buzz/${post.id}`"
        class="inline-flex items-center px-4 py-2 font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transform hover:scale-105 transition-all duration-300 group relative overflow-hidden"
      >
        <span class="relative z-10">Read More</span>
        <Icon
          name="heroicons:arrow-right"
          class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform relative z-10"
        />
        <span
          class="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        ></span>
      </NuxtLink>
    </div>
  </article>
</template>

<script setup lang="ts">
interface Category {
  id: number;
  attributes: {
    name: string;
    slug: string;
    about: string;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
  };
}

interface Props {
  post: any; // or define a more specific type if needed
  isFeatured?: boolean;
}

withDefaults(defineProps<Props>(), {
  isFeatured: false,
});
</script>
